import { useState, type ComponentProps } from 'react';
import { evaluateAmount, formatMinor, minorToPlain, toMinor } from '@/lib/money';
import { cn } from '@/lib/utils';

/**
 * AmountInput (document 05): right-aligned tabular numerals; accepts arithmetic
 * (`120*1.05`) evaluated on blur or `=`. `value` is a plain decimal ("1234.50") or "".
 */
export function AmountInput({
  value,
  onValueChange,
  className,
  invalid,
  exp = 2,
  ...props
}: Omit<ComponentProps<'input'>, 'value' | 'onChange'> & {
  value: string;
  onValueChange: (plain: string) => void;
  invalid?: boolean;
  /** Decimal places of the currency (3 for KWD, 0 for JPY). */
  exp?: number;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const commit = (text: string) => {
    setEditing(null);
    if (text.trim() === '') return onValueChange('');
    const minor = evaluateAmount(text, exp);
    if (minor === null || minor < 0n) return onValueChange(value); // keep the previous value
    onValueChange(minor === 0n ? '' : minorToPlain(minor, exp));
  };
  const minor = value ? toMinor(value, exp) : null;
  const shown = editing ?? (minor ? formatMinor(minor, exp) : '');
  return (
    <input
      {...props}
      inputMode="decimal"
      autoComplete="off"
      value={shown}
      aria-invalid={invalid || undefined}
      onFocus={(e) => {
        setEditing(value);
        props.onFocus?.(e);
        requestAnimationFrame(() => e.target.select());
      }}
      onChange={(e) => setEditing(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === '=' || e.key === 'Enter') {
          e.preventDefault();
          commit(editing ?? value);
          if (e.key === 'Enter') e.currentTarget.blur();
        }
        props.onKeyDown?.(e);
      }}
      onBlur={(e) => {
        commit(editing ?? value);
        props.onBlur?.(e);
      }}
      className={cn(
        'tnum h-9 w-full rounded-md border border-input bg-card px-2 text-right text-sm outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive',
        className,
      )}
    />
  );
}
